import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import {
  ATTENTION_WELL_DOT_GLOW_LAYER,
  ATTENTION_WELL_DOT_LAYER,
  ATTENTION_WELL_JUMP_GLOW_LAYER,
  ATTENTION_WELL_JUMP_LAYER,
  ATTENTION_WELL_LAYER,
  ATTENTION_WELL_POINT_LAYER,
  SELECTED_SINGLE_WELL_GLOW_LAYER,
  SELECTED_SINGLE_WELL_LAYER,
  SELECTED_SINGLE_WELL_POINT_GLOW_LAYER,
  SELECTED_SINGLE_WELL_POINT_LAYER,
  STICK_LAYER,
  WELL_HIGHLIGHT_LAYERS,
  WELL_LAYER,
  WELL_LAYER_POINT
} from "constants/mapLayers.constants";
import mapboxglx from "mapbox-gl";
import { RootState } from "store/rootReducer";
import {
  selectIpdbControlWellsLoaded,
  selectIpdbGeoModelLoaded,
  selectIsDebuggingMap,
  useMapStore
} from "stores/map";

import { GL_DRAW_PREFIX } from "components/map/utils/drawConstants";
import { safeMoveLayers } from "components/map/utils/layerOperations";
import { useEditableProjectLayerList } from "components/project/layers/hooks";
import { useProjectContext } from "components/project/projects/context";

import { useMapContext } from "../useMapContext";
import {
  debugMapboxSorting,
  doesMapboxLayerMatchOrderedLayer,
  getLayerListMergedWithMapbox,
  getMapLayersMatchedToLayerList
} from "./util";

/**  A reduced list layer, with only the properties we need to manage sorting */
export interface IOrderedLayer {
  name: string;
  order: number;
  shapefileId?: string;
  projectShapefileId?: string;
  projectLayerType?: string;
}

// The highlighted well's glow and black core stay above every well layer
// whatever order the user gives the layer list. Every glow sits under every
// core, so no glow tints a core where two highlighted wells meet. Pinned in this
// order, directly under the gl-draw layers.
const PINNED_TOP_LAYERS: string[] = [
  ATTENTION_WELL_JUMP_GLOW_LAYER,
  ATTENTION_WELL_DOT_GLOW_LAYER,
  SELECTED_SINGLE_WELL_GLOW_LAYER,
  SELECTED_SINGLE_WELL_POINT_GLOW_LAYER,
  ATTENTION_WELL_JUMP_LAYER,
  ATTENTION_WELL_DOT_LAYER,
  SELECTED_SINGLE_WELL_LAYER,
  SELECTED_SINGLE_WELL_POINT_LAYER
];

// The hovered well's black core sits over its series' highlight. Under it, the
// highlight's equal width covered the core completely the moment the series
// drew, so a hover showed black and then went red.
const WELL_HIGHLIGHT_STACK: string[] = [
  ...WELL_HIGHLIGHT_LAYERS,
  ATTENTION_WELL_LAYER,
  ATTENTION_WELL_POINT_LAYER
];

interface IUseLayerSorterForMapboxParams {
  mapbox: mapboxglx.Map;
}
export function useManageMapboxLayerSorting({ mapbox }: IUseLayerSorterForMapboxParams) {
  const {
    isMapInitialized,
    isStyleLoaded,
    isLoading: hasMapDependenciesLoading
  } = useMapContext();

  const { selectedProjectId } = useProjectContext();

  const layerList = useEditableProjectLayerList({
    projectId: selectedProjectId,
    filters: ["unselectedLayers"]
  });

  /**
   * Order needs to be in reverse as this is is how mapbox orders the layers, first layer on the layer list, is the last layer on the map
   */
  const layerListSorted = useMemo(() => {
    // Left out so the sort and the pin below never fight over them.
    const tempArray = JSON.parse(JSON.stringify(layerList.data)).filter(
      (layer) => !PINNED_TOP_LAYERS.includes(layer.name)
    );
    const newList = tempArray.sort((a, b) => b.order - a.order);

    newList.forEach((layer, index) => {
      layer.order = index;
    });
    return newList;
  }, [layerList.data]);

  const currentShapefileFeatureProperty = useSelector(
    (state: RootState) => state.project.currentShapefileFeatureProperty
  );
  const currentShapefileIdForFeatureProperty = useSelector(
    (state: RootState) => state.project.currentShapefileIdForFeatureProperty
  );

  const ipdbGeoModelLoaded = useMapStore(selectIpdbGeoModelLoaded);
  const ipdbControlWellsLoaded = useMapStore(selectIpdbControlWellsLoaded);

  const isDebuggingMap = useMapStore(selectIsDebuggingMap);
  /**
   * Manage sorting mapbox layers to match the the ordered layer list
   */
  useEffect(() => {
    if (!isMapInitialized || !isStyleLoaded || !mapbox || hasMapDependenciesLoading) {
      return;
    }

    // Mapbox load state is checked inside performSort() rather than here, so the
    // sourcedata/idle listeners below attach even while a source is still mid-load.
    const performSort = () => {
      if (!mapbox?.isStyleLoaded?.() || !mapbox?.getStyle?.()?.layers) {
        return;
      }

      if (isDebuggingMap) {
        // eslint-disable-next-line no-console
        console.time("~~ Sorting time: ");
      }
      const mapboxLayers = mapbox.getStyle().layers;

      const unorderedMapLayers = getMapLayersMatchedToLayerList({
        layerList: layerListSorted,
        mapboxLayers: mapboxLayers
      });

      const orderedLayerList = getLayerListMergedWithMapbox({
        layerList: layerListSorted,
        mapboxLayers: unorderedMapLayers
      });

      // Both lists above only contain layers that exist on the map, so a checked
      // shapefile whose layers haven't been (re-)added yet would pass the comparison
      // vacuously - treat it as a mismatch instead. Entries without a shapefile id
      // (label toggles, dependant layers) never get their own mapbox layer and are
      // excluded so they can't permanently disable the short-circuit.
      const hasCheckedLayerMissingFromMapbox = layerListSorted.some(
        (orderedLayer) =>
          (orderedLayer.projectShapefileId || orderedLayer.shapefileId) &&
          !mapboxLayers.some((layer) =>
            doesMapboxLayerMatchOrderedLayer(layer, orderedLayer)
          )
      );

      const isMapboxMatchingOrderedLayerList =
        !hasCheckedLayerMissingFromMapbox &&
        JSON.stringify(unorderedMapLayers) === JSON.stringify(orderedLayerList);

      // Declared outside the sort block because the stick-pinning block below also
      // needs to know whether `mapboxLayers` is still an accurate snapshot.
      let hasMovedALayer = false;

      if (!isMapboxMatchingOrderedLayerList) {
        // Create a working copy to track current positions as we move layers
        const currentPositions = [...unorderedMapLayers];

        orderedLayerList.forEach((orderedLayer) => {
          // Find where this layer currently is in our tracking array
          const currentIndex = currentPositions.findIndex(
            (l) => l.name === orderedLayer.name
          );

          if (
            currentIndex === -1 ||
            !mapbox.getLayer(orderedLayer?.name) ||
            currentIndex === orderedLayer.order
          ) {
            // If layer is already in the correct position, skip it
            // Layer doesn't exist, skip it
            return;
          }

          // Determine the target position accounting for the removal of the current layer
          // When we splice out the current layer, indices shift
          let adjustedTargetIndex = orderedLayer.order;
          if (currentIndex < orderedLayer.order) {
            // If we're moving forward, target index decreases by 1 after removal
            adjustedTargetIndex = orderedLayer.order;
          }

          // Find the layer that will be at our target position after we remove current layer
          let targetLayer = null;
          if (adjustedTargetIndex < currentPositions.length) {
            // Create temp array to simulate the removal
            const tempPositions = [...currentPositions];
            tempPositions.splice(currentIndex, 1);
            targetLayer = tempPositions[adjustedTargetIndex];
          }

          // Move the layer in Mapbox
          if (!targetLayer) {
            // Moving to the very end - call moveLayer without beforeId to move to top of rendering order
            mapbox.moveLayer(orderedLayer.name);
            if (isDebuggingMap && orderedLayer.name?.includes("Geo Model")) {
              // eslint-disable-next-line no-console
              console.log(
                `"${orderedLayer.name}" moved to end (position ${orderedLayer.order})`
              );
            }
          } else {
            // Moving to a specific position - move before the target layer
            mapbox.moveLayer(orderedLayer.name, targetLayer.name);
          }
          hasMovedALayer = true;

          // Update our tracking array to reflect the move
          const [movedLayer] = currentPositions.splice(currentIndex, 1);
          currentPositions.splice(orderedLayer.order, 0, movedLayer);

          // Update order values in tracking array
          currentPositions.forEach((layer, index) => {
            layer.order = index;
          });
        });

        // Move all gl-draw layers to the very end to keep lasso on top - but only
        // when a layer actually moved. moveLayer marks the style changed even for a
        // no-op move, which re-fires "idle" and would loop performSort forever while
        // a checked shapefile is still missing from the map (e.g. failed tile load).
        if (hasMovedALayer) {
          const drawLayerIds = mapbox
            .getStyle()
            .layers.filter((layer) => layer.id.startsWith(GL_DRAW_PREFIX))
            .map((layer) => layer.id);
          safeMoveLayers(mapbox, drawLayerIds);
        }
      }

      // Sticks are never in the project layer list, so the sort loop above never
      // moves them while every listed layer shuffles around them — leaving sticks
      // above the XDA cross-section line at zoom >= 8. Re-pin them directly ABOVE
      // WELL_LAYER, which is where map init leaves them: getMapLayers declares
      // Wells then Sticks, both are added with no beforeId, and Style#addLayer
      // appends to the end of the render order (topmost).
      //
      // Above, not below, and the direction is load-bearing. Mapbox sorts
      // queryRenderedFeatures results by walking the render order in reverse, so
      // features[0] comes from the topmost layer - and four consumers read
      // features[0]: useMapClickSelection, useMapHoverLegend, useWellDetailHover
      // and getSingleWell. Wells and sticks draw disjoint feature sets
      // (complementary `group == "stick"` filters) but overlap in screen space:
      // the ±2px click / ±3px hover buffers cover roughly a kilometre of ground at
      // zoom 8, so an overlapping well is near-certain in the zoom 8-12 band.
      // Pinning sticks under Wells would silently hand a click on a stick to the
      // well beside it; pinning above preserves stick-wins pick priority. The XDA
      // fix still holds - "XDA Line" sorts above Wells through the layer list, so
      // wellIndex + 1 lands sticks below it.
      //
      // The slot below Wells is contended anyway: HZ_WELL_RASTER_LAYER
      // (useMapTileSourceManager), BASE_WELL_LAYER (MapboxWrapper), and
      // BASE_WELL_RASTER_LAYER (MapboxWrapper, EVA-35) all add themselves with
      // `beforeId: WELL_LAYER` (or, for the base raster, HZ_WELL_RASTER_LAYER
      // when it's already present) and legitimately want a slot below Wells.
      // Nothing competes for the slot above.
      //
      // ponytail: pinned rather than adding Sticks to the editable layer list —
      // do that instead if users ever need to order sticks independently.
      // Latent collision: STICK_LAYER is already in `dependantLayers`
      // (constants/mapLayers.constants.ts) and getLayerListMergedWithMapbox's
      // addDependantMapLayer branch already pins dependant layers to their parent.
      // That branch is dead today only because "Sticks" never reaches the editable
      // project layer list (createEditableLayerList doesn't emit it, and it is in
      // neither DEFAULT_CHECKED_LAYERS nor the project tree root map) - if it ever
      // does, the sort loop above and this pin block would fight over Sticks'
      // position, alternating on every idle pass.
      //
      // Reuse the snapshot taken above unless the sort loop (or the gl-draw shuffle
      // it guards) actually moved something, which would leave it stale. getStyle()
      // deep-serialises every layer and source, and this block runs on every "idle"
      // and metadata "sourcedata" event - the overwhelmingly common case being that
      // nothing moved.
      const currentStyleLayers = hasMovedALayer ? mapbox.getStyle().layers : mapboxLayers;
      const stickIndex = currentStyleLayers.findIndex(
        (layer) => layer.id === STICK_LAYER
      );
      const wellIndex = currentStyleLayers.findIndex((layer) => layer.id === WELL_LAYER);
      // Guard the no-op move: Style#moveLayer sets `_changed = true` before its
      // `id === before` early-out, so even a no-op dirties the style and re-fires
      // "idle", which would loop performSort forever.
      if (stickIndex !== -1 && wellIndex !== -1 && stickIndex !== wellIndex + 1) {
        // An undefined beforeId (Wells is topmost) sends sticks to the very top,
        // which is still directly above Wells. Inside this branch the target is
        // never STICK_LAYER itself, and after the move stickIndex === wellIndex + 1,
        // so the next pass no-ops.
        mapbox.moveLayer(STICK_LAYER, currentStyleLayers[wellIndex + 1]?.id);
        hasMovedALayer = true;
      }

      // Highlighted series draw directly above every well layer, under whatever
      // the layer list puts above Wells (XDA line, labels).
      const wellStackIds = (hasMovedALayer ? mapbox.getStyle().layers : mapboxLayers).map(
        (layer) => layer.id
      );
      const highlightIds: string[] = WELL_HIGHLIGHT_STACK.filter((id) =>
        wellStackIds.includes(id)
      );
      const otherIds = wellStackIds.filter((id) => !highlightIds.includes(id));
      const wellTop = Math.max(
        otherIds.indexOf(WELL_LAYER),
        otherIds.indexOf(STICK_LAYER),
        otherIds.indexOf(WELL_LAYER_POINT)
      );
      const wellTopIndex = wellStackIds.indexOf(otherIds[wellTop]);
      const currentAbove = wellStackIds.slice(
        wellTopIndex + 1,
        wellTopIndex + 1 + highlightIds.length
      );
      // Same no-op guard as the sticks: an unneeded move re-fires "idle".
      if (wellTop !== -1 && currentAbove.join(",") !== highlightIds.join(",")) {
        highlightIds.forEach((id) => mapbox.moveLayer(id, otherIds[wellTop + 1]));
        hasMovedALayer = true;
      }

      const pinnedStyleLayers = hasMovedALayer ? mapbox.getStyle().layers : mapboxLayers;
      const pinnedIds = PINNED_TOP_LAYERS.filter((id) =>
        pinnedStyleLayers.some((layer) => layer.id === id)
      );
      const firstDrawIndex = pinnedStyleLayers.findIndex((layer) =>
        layer.id.startsWith(GL_DRAW_PREFIX)
      );
      const pinEnd = firstDrawIndex === -1 ? pinnedStyleLayers.length : firstDrawIndex;
      const currentPinnedIds = pinnedStyleLayers
        .slice(pinEnd - pinnedIds.length, pinEnd)
        .map((layer) => layer.id);
      // Same no-op guard as the sticks: an unneeded move re-fires "idle".
      if (currentPinnedIds.join(",") !== pinnedIds.join(",")) {
        const beforeId = pinnedStyleLayers[firstDrawIndex]?.id;
        pinnedIds.forEach((id) => mapbox.moveLayer(id, beforeId));
      }

      if (isDebuggingMap) {
        // eslint-disable-next-line no-console
        console.log("~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~");
        // eslint-disable-next-line no-console
        console.timeEnd("~~ Sorting time: ");
        debugMapboxSorting(
          mapbox.getStyle().layers,
          mapbox.getStyle().sources,
          orderedLayerList
        );
      }
    };

    performSort();

    const handleSourceData = (e: mapboxglx.MapSourceDataEvent) => {
      if (e.sourceDataType !== "metadata") return;
      performSort();
    };

    /**
     * Layer additions are asynchronous - a layer passed to addLayer() may not appear
     * in getStyle().layers immediately. sourcedata/metadata catches layers landing
     * while a source is still loading; idle catches re-added sources that never emit
     * a metadata event. performSort's early-outs make repeat calls no-ops.
     */
    mapbox.on("sourcedata", handleSourceData);
    mapbox.on("idle", performSort);
    return () => {
      if (mapbox) {
        mapbox.off("sourcedata", handleSourceData);
        mapbox.off("idle", performSort);
      }
    };
  }, [
    layerListSorted,
    isMapInitialized,
    isStyleLoaded,
    hasMapDependenciesLoading,
    currentShapefileFeatureProperty,
    currentShapefileIdForFeatureProperty,
    ipdbGeoModelLoaded,
    ipdbControlWellsLoaded
  ]);
}
